/**
 * The camera, asked for once for the whole app.
 *
 * Selfie Mode used to open the phone's own camera app through <input capture>, which hands back
 * a photo and grants the PAGE nothing: the browser was never asked for the camera, so there was
 * no permission afterwards for anything else to use. Eyelid, running inside this same app to
 * show the owner what a tenant's camera sees, then had to ask all over again — which is exactly
 * the second consent nobody wants.
 *
 * So the camera is opened here instead, by the page, with getUserMedia. The grant belongs to the
 * origin and outlives the tab, and the fact that it was given is written under the key Eyelid's
 * tenant piece already reads (mcw_eyelid_consent_v1), so consent given once in Selfie Mode is
 * consent for the app: no second prompt of ours, and no second prompt from the browser either,
 * since it is one origin and one grant.
 */

// Remote assist, generated by OneAdmin (src/oneadmin/eyelid.js). Same consent key as this
// module, deliberately: one camera consent for the whole app.
import { primeRemoteAssist, setCameraLive } from './remoteAssist';

// The same key OneAdmin's Eyelid client uses (src/lib/eyelid.ts), so either side may write it
// and both sides believe it. Cleared only by uninstalling the app or clearing site data, which
// is the intended lifetime of a standing consent.
const CONSENT_KEY = 'mcw_eyelid_consent_v1';

export const hasCameraConsent = () => {
  try { return localStorage.getItem(CONSENT_KEY) === '1'; } catch { return false; }
};

export const grantCameraConsent = () => {
  try { localStorage.setItem(CONSENT_KEY, '1'); } catch { /* private mode: asked again next time */ }
};

/**
 * Why a camera could not be opened, in the four cases worth telling apart. "denied" is the only
 * one the person can do anything about from their browser settings; saying so for any of the
 * others is advice that cannot work.
 *   insecure    the page is on plain http, where no browser allows a camera at all
 *   missing     the device has no camera
 *   busy        something else is holding it
 *   unavailable this browser has no getUserMedia
 */
export class CameraError extends Error {
  // `detail` is the browser's OWN error name, carried through untouched. The five reasons above
  // are a translation for the person reading them, and a translation loses the original: when
  // somebody reports "it says blocked but I allowed it", the only thing worth knowing is which
  // of NotAllowedError, SecurityError or AbortError the browser actually raised, and that had
  // been thrown away at the point where it was still known.
  constructor(reason, message, detail) {
    super(message);
    this.name = 'CameraError';
    this.reason = reason;
    this.detail = detail || '';
  }
}

/**
 * Has this origin already been given the camera? Asked so the app can use the camera WITHOUT
 * producing a second permission popup — the one the person already answered, for Eyelid, is the
 * only one there should be. A browser without the Permissions API answers no, which is the safe
 * way round: the app then takes its picture by a route that needs no permission at all.
 */
export async function cameraAlreadyGranted() {
  try {
    const st = await navigator.permissions?.query({ name: 'camera' });
    return st?.state === 'granted';
  } catch { return false; }
}

/** False when this document is framed and the frame was not given the camera. */
export function cameraAllowedHere() {
  try {
    if (typeof window === 'undefined' || window.top === window.self) return true;   // not framed
    const fp = document.featurePolicy || document.permissionsPolicy;
    if (fp?.allowsFeature) return fp.allowsFeature('camera');
    return true;   // no way to ask — try, and let getUserMedia have the last word
  } catch { return true; }
}

const MESSAGES = {
  'blocked-here': 'This window is not allowed to open the camera.',
  insecure: 'The camera needs a secure (https) address. This page is on plain http, where no browser allows one.',
  missing: 'No camera was found on this device.',
  busy: 'The camera is in use by something else — close it and try again.',
  // Two different situations arrive as the same NotAllowedError: the prompt was dismissed rather
  // than answered, and the camera is blocked for this site. Only the second is a settings
  // problem, so leading with the settings advice was wrong half the time — and reads as an
  // accusation when somebody has just tapped Allow. Tap again first; settings only if asking
  // has stopped working.
  denied: 'The camera was not allowed. Tap again and choose Allow — and if the browser no longer asks, unblock it from the padlock in the address bar (in the installed app, from its site settings).',
  unavailable: 'This browser cannot open the camera.',
};
export const cameraMessage = (reason) => MESSAGES[reason] || MESSAGES.unavailable;

/**
 * Open the camera. Resolves with a MediaStream and records the standing consent; rejects with a
 * CameraError carrying one of the reasons above.
 *
 * @param {'user'|'environment'} facing  front camera by default — a selfie is of the person
 *                                       holding the phone, not of what it is pointed at.
 */
// ── One request at a time ────────────────────────────────────────────────────────────────
// A browser will show ONE camera prompt and, while it is up, refuse any second request out of
// hand — instantly, with the same NotAllowedError a person's "no" produces. So two overlapping
// asks look exactly like this: the prompt sitting there waiting to be answered, and, over the
// top of it, a notice saying the camera is blocked. Which is what was being reported, and the
// giveaway is that both are on screen at once — a real refusal cannot arrive before the prompt
// has been answered.
//
// The app could reach the camera from several places — the Add Image menu, Selfie Mode's own
// button, the flip button, the "use in app" dialog — and nothing stopped two of them landing
// together. Now the first one owns the request and everyone else waits for its answer: same
// camera, so a second ask could never have been anything but a way to lose the first.
let pendingOpen = null;

// ── Saying when the camera is actually on ────────────────────────────────────────────────
// App Control shows a device as Live, and until now nothing told it whether this app's camera
// was running — so it had nothing true to go on. This is that signal, and it is counted rather
// than a flag because flipping front to back can hold two streams for a moment and "off" must
// not be reported in the middle of it.
//
// stop() does not raise 'ended' — that is for a source going away on its own — so closing has
// to say so itself. Hence the mark on the stream: whoever lets go of it, it is only counted
// down once.
let liveStreams = 0;
const liveChanged = () => { try { setCameraLive(liveStreams > 0); } catch { /* assist absent */ } };
const countLive = (stream) => {
  if (!stream || stream.__csLive) return;
  stream.__csLive = true;
  liveStreams++;
  liveChanged();
  try {
    const ended = () => { if (!stream.getTracks().some(t => t.readyState === 'live')) releaseLive(stream); };
    stream.getTracks().forEach(t => t.addEventListener('ended', ended));
  } catch { /* an unusual stream: closeCamera still counts it down */ }
};
const releaseLive = (stream) => {
  if (!stream || !stream.__csLive) return;
  stream.__csLive = false;
  liveStreams = Math.max(0, liveStreams - 1);
  liveChanged();
};

export async function openCamera(facing = 'user') {
  if (pendingOpen) {
    // Same camera wanted: share the one answer. A different one: let the first finish, so the
    // second is a fresh request rather than a refusal.
    if (pendingOpen.facing === facing) return pendingOpen.promise;
    try { await pendingOpen.promise; } catch { /* its failure is not this call's failure */ }
  }
  const promise = openCameraOnce(facing);
  pendingOpen = { facing, promise };
  try { return await promise; }
  finally { if (pendingOpen?.promise === promise) pendingOpen = null; }
}

async function openCameraOnce(facing) {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    throw new CameraError('insecure', MESSAGES.insecure);
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    // No getUserMedia at all is usually not this browser's shortcoming — it is the address.
    // Browsers withhold the whole of mediaDevices on a plain http origin, so "this browser
    // cannot open the camera" was blaming the wrong thing on every LAN address.
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      throw new CameraError('insecure', MESSAGES.insecure);
    }
    throw new CameraError('unavailable', MESSAGES.unavailable);
  }
  // A page inside a frame gets the camera only if the frame was written with allow="camera".
  // Without it the browser refuses with the same NotAllowedError a person's "no" produces, and
  // telling somebody to change a setting they do not have is worse than useless — so this case
  // is recognised for what it is and handed back as 'blocked-here'.
  if (!cameraAllowedHere()) throw new CameraError('blocked-here', MESSAGES['blocked-here']);
  try {
    // ── Ask for the camera, then settle for one ─────────────────────────────────────────
    // facingMode as a preference alone was not enough, and the reason is the resolution. A
    // browser picks the settings closest to everything asked for AT ONCE, so on an iPad the
    // back camera — which reaches 1920 and more — scored better against "ideally 1920 by 1920"
    // than the front one, which does not, and "front, ideally" lost to "sharper" every time.
    // Selfie Mode opened the back camera on every iPad, whatever it was asked for.
    //
    // Worse, 1920 by 1920 is a shape no camera has. It was meant as "as much detail as you
    // can", and it read as "as square as you can", which is what let it decide the lens.
    //
    // So: ask for the camera EXACTLY first, where the device set is narrowed before resolution
    // is looked at and the lens cannot be traded away. A laptop's single webcam usually reports
    // no facing at all and refuses that — which is the case the preference below is for, and
    // why the two are tried in order rather than one being chosen.
    const attempts = [
      { facingMode: { exact: facing }, width: { ideal: 1920 } },
      { facingMode: facing, width: { ideal: 1920 } },
      { facingMode: facing },
      true,
    ];
    let stream = null, lastErr = null;
    for (const video of attempts) {
      try { stream = await navigator.mediaDevices.getUserMedia({ video, audio: false }); break; }
      catch (e) {
        lastErr = e;
        // Only "nothing here matches that" is worth stepping down for. A refusal, a camera
        // somebody else is holding, a page not allowed one — trying again in four more shapes
        // changes nothing and, on the browsers that prompt per request, asks four more times.
        if (e?.name !== 'OverconstrainedError' && e?.name !== 'NotFoundError') throw e;
      }
    }
    if (!stream) throw lastErr || new Error('No camera matched');
    grantCameraConsent();
    countLive(stream);
    // The permission was just given here, by their own tap. Register the device with remote assist
    // at that moment, so App Control has something to connect to and the person is not asked a
    // second time later. Fire-and-forget: this must never delay the camera they asked for.
    void primeRemoteAssist();
    return stream;
  } catch (err) {
    const n = err?.name || '';
    if (typeof console !== 'undefined') console.warn('[camera]', n, err?.message || '');
    if (n === 'NotAllowedError' || n === 'SecurityError') throw new CameraError('denied', MESSAGES.denied, n);
    if (n === 'NotFoundError' || n === 'OverconstrainedError') throw new CameraError('missing', MESSAGES.missing, n);
    if (n === 'NotReadableError' || n === 'AbortError') throw new CameraError('busy', MESSAGES.busy, n);
    throw new CameraError('unavailable', err?.message || MESSAGES.unavailable, n);
  }
}

/** Let go of every track, so the recording light goes out the moment the camera is closed. */
export const closeCamera = (stream) => {
  try { stream?.getTracks?.().forEach(t => t.stop()); } catch { /* already gone */ }
  releaseLive(stream);
};
